import { Tooltip } from "@mui/material";
import styled from "styled-components";

export type ActionTone = "approve" | "reject" | "warn" | "info" | "neutral";

// Fixed colors on purpose: these sit on top of photos and video, so they
// must not shift with the light/dark theme.
const TONE_BACKGROUND: Record<ActionTone, string> = {
  approve: "#1faa63",
  reject: "#e5484d",
  warn: "#f08c00",
  info: "#3b82f6",
  neutral: "rgba(17, 19, 24, 0.72)",
};

const Button = styled.button<{ $tone: ActionTone; $size: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${(props) => props.$size}px;
  height: ${(props) => props.$size}px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: ${(props) => TONE_BACKGROUND[props.$tone]};
  color: #fff;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35),
    inset 0 0 0 1px rgba(255, 255, 255, 0.16);
  backdrop-filter: ${(props) =>
    props.$tone === "neutral" ? "blur(6px)" : "none"};
  transition: transform 0.12s ease, filter 0.12s ease;

  &:hover:not(:disabled) {
    transform: scale(1.1);
    filter: brightness(1.1);
  }

  &:active:not(:disabled) {
    transform: scale(0.96);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  svg {
    width: ${(props) => Math.round(props.$size * 0.5)}px;
    height: ${(props) => Math.round(props.$size * 0.5)}px;
  }
`;

// Round icon button for moderation actions on top of media. `label` is both
// the tooltip and the accessible name, so every action explains itself.
const ActionButton: React.FC<{
  label: string;
  tone?: ActionTone;
  size?: number;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
}> = ({ label, tone = "neutral", size = 36, disabled, onClick, children }) => {
  return (
    <Tooltip title={label} placement="top" arrow>
      <Button
        type="button"
        $tone={tone}
        $size={size}
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
      >
        {children}
      </Button>
    </Tooltip>
  );
};
export default ActionButton;
